// Global code
if (!String.prototype.replaceAll) {
    String.prototype.replaceAll = function (str, newStr) {

        // If a regex pattern
        if (Object.prototype.toString.call(str).toLowerCase() === '[object regexp]') {
            return this.replace(str, newStr);
        }

        // If a string
        return this.replace(new RegExp(str, 'g'), newStr);

    };
}

if (!__DEV__) {
    console.log = () => { };
}

// case-insensitive substring predicate
const ciin = (x, y) => y.toLowerCase().indexOf(x.toLowerCase()) > -1;

const isChrome = ciin('chrome', navigator.userAgent);
const isSamsungBrowser = ciin('SamsungBrowser', navigator.userAgent);
const isXiaomiBrowser = ciin('MiuiBrowser', navigator.userAgent);
const isAndroid = ciin('Android', navigator.userAgent);


const redirectCondition = isSamsungBrowser || isXiaomiBrowser || (!isChrome && isAndroid);

if (redirectCondition) {
    let deeplink = `googlechrome://navigate?url=${window.location.toString()}`;
    let intent = `intent://navigate?url=${window.location.toString()}#Intent;scheme=googlechrome;package=com.android.chrome;end`;

    // try several methods until one ends up working
    window.location = intent;
    window.location = deeplink.toString();
    window.location.assign(deeplink.toString());
    window.open(deeplink.toString());
}

// React && Own-lib
import { Spacing } from '@styles';
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Image, Linking, SafeAreaView } from 'react-native';
import { MenuScreen } from './src/scenes';

// Providers
import { LanguageProvider, useLanguage } from '@languageProvider';
import ThemeProvider, { useTheme } from '@themeProvider';

// Native libs
// import * as Font from 'expo-font';

import { ProductNode, ProductPack, Promotion } from '@datamodel';
import { ModalProvider, SnackbarProvider, useModal } from '@lib';
import service, { Events, Orders, Places, Promotions } from '@services';
import { TRACKING_CONFIG, sinkhole } from '@services/trackings';
import { setDeviceDimensions } from '@styles/commons';
import { record } from './src/services/trackings';
import { getCartRecovery, setBeaconIds } from './src/utils/storage';

const StatefulApp = (props) => {
    const [appReady, setAppReady] = useState(false);
    const [colorsReady, setColorsReady] = useState(false);
    const [state, setState] = useState({
        placeId: '', eventId: '', place: {},
        menu: {}, packs: {}, pickupBars: {}, promotions: {},
        isAuth: false
    });
    const { translate } = useLanguage();
    const { pushAlert } = useModal();
    const { useColor, setColors } = useTheme();
    const [backgroundColor, primaryColor] = useColor(['background', 'primary'])

    const refreshMenu = useCallback(async () => {
        try {
            // Get menu
            const menuInfo = await Events.getMenu(state.eventId);

            // Get and format packs
            const { packRoots, menu } = menuInfo;
            const { packs } = await Events.getPacks(state.eventId);
            ProductNode.propagatePrices(menu);

            const fullMenu = ProductNode._addChildren(menu, packRoots);
            const fullPacks = ProductPack.collectionAddInformation(packs, fullMenu);


            setState({
                ...state,
                menu: fullMenu,
                packs: fullPacks
            })
        } catch (error) {
            console.log(error);
        }
    }, [state.eventId]);

    const addPromotions = (promotions) => setState(prev => ({
        ...prev,
        promotions: {
            ...prev.promotions,
            ...promotions
        }
    }));

    useEffect(() => {
        const prepareColors = () => {
            try {
                if (props.palette) {
                    const palette = JSON.parse(props.palette);
                    setColors(palette);

                    // Using the primary color from the palette rather than place object
                    const primaryColor = palette.primary?.base?.value;

                    // Set meta color in Chrome
                    if (primaryColor) {
                        const themeMeta = document.createElement('meta');
                        themeMeta.setAttribute('name', 'theme-color');
                        themeMeta.setAttribute('content', primaryColor);
                        document.head.append(themeMeta);
                    }
                }
            } catch (e) {
                console.error('Error while setting colors:', e);
            }
        }

        prepareColors();
        setColorsReady(true);
    }, [colorsReady]);

    useEffect(() => {
        const prepare = async () => {
            try {
                let eventId, placeId, profile;
                const url = await Linking.getInitialURL();
                const urlObj = new URL(url);

                eventId = props.event_id
                placeId = props.place_id
                if (!eventId || !placeId) {
                    throw "event_not_found";
                }

                setBeaconIds({ uid: props.userBeacon });

                // Get menu
                let menuInfo;
                try {
                    menuInfo = await Events.getMenu(eventId)
                } catch (e) {
                    console.error('No menu found', e);
                }

                const { event } = await Events.get(eventId);

                // Get place
                console.log('GET PLACE');
                const { place: placeInfo, pickupBars, business, availableEvents } = await Places.get(placeId);
                console.log('GOT PLACE');

                if (availableEvents.indexOf(eventId) < 0 || !menuInfo) {
                    window.location = service._build_url(`tripay/${placeInfo.slug}/qr/`);
                }

                // Get and format packs
                const { packRoots, menu } = menuInfo;
                const { packs } = await Events.getPacks(eventId);
                console.log(menu);
                ProductNode.propagatePrices(menu);
                const fullMenu = ProductNode._addChildren(menu, packRoots);
                const fullPacks = ProductPack.collectionAddInformation(packs, fullMenu);

                // Get promotions
                const { promotions } = await Promotions.list({
                    festea: true,
                    place: placeId
                });


                // Set tracking config
                TRACKING_CONFIG.path = urlObj.pathname;
                TRACKING_CONFIG.hostname = urlObj.hostname;
                TRACKING_CONFIG.slug = placeInfo.slug;
                TRACKING_CONFIG.place = placeId;
                TRACKING_CONFIG.event = eventId;
                TRACKING_CONFIG.sinkholeRealm = props.sinkholeRealm;
                TRACKING_CONFIG.sinkholeApiKey = props.sinkholeApiKey;
                TRACKING_CONFIG.userBeacon = props.userBeacon;
                sinkhole.api_key = props.sinkholeApiKey;
                sinkhole.realm = props.sinkholeRealm;

                // Check if serialized order is recoverable
                const {
                    menuRoot: lastMenuRoot,
                    cart: lastCart,
                    orderId: lastOrderId,
                } = getCartRecovery() || {
                    menuRoot: null,
                    cart: null,
                    orderId: null,
                };

                let initialCart;
                if (fullMenu.nodeId === lastMenuRoot) {
                    console.log('Recovering cart');
                    try {
                        await Orders.isRecoverable(lastOrderId);
                        initialCart = lastCart;
                        record('order_recovery_attempt', {
                            success: true,
                            orderId: lastOrderId
                        }, false)
                    } catch (e) {
                        console.error(e, 'Cart is not recoverable');
                        record('order_recovery_attempt', {
                            success: false,
                            orderId: lastOrderId
                        }, false)
                    }
                }

                setState({
                    place: placeInfo,
                    placeId, eventId, event,
                    promotions: Promotion.collectionFilterByPlaceId(promotions, placeId),
                    menu: fullMenu,
                    packs: fullPacks, ...placeInfo, pickupBars, business,
                    isAuth: !!profile, // TODO: Change this later
                    profile,
                    popupSponsorPhoto: props.popupSponsorPhoto,
                    popupSponsorUrl: props.popupSponsorUrl,
                    popupSponsorCta: props.popupSponsorCta,
                    initialCart
                });


                setAppReady(true);
            } catch (e) {
                if (e === "event_not_found") {
                    pushAlert({ title: 'Error', text: translate('eventNotFound'), actions: [] })
                } else {
                    pushAlert({ title: 'Error', text: translate('somethingWentWrong'), actions: [] });
                }
                console.warn(e);
            }
        }

        prepare();
    }, []);


    if (!appReady || !colorsReady) {
        return <SafeAreaView style={{ flexGrow: 1, backgroundColor, alignItems: 'center', justifyContent: 'center' }}>
            <Image source={require('@assets/images/FesteaPOS.webp')} style={{ width: 150, height: 150, marginBottom: Spacing.M }} />
            {colorsReady && <ActivityIndicator size="large" color={primaryColor} />}
        </SafeAreaView>;
    }

    return (<MenuScreen {...state} refreshMenu={refreshMenu} addPromotions={addPromotions} />)
}

export function App(props) {
    setDeviceDimensions(props.HTMLElement);
    return (
        <LanguageProvider>
            <ThemeProvider>
                <SnackbarProvider>
                    <ModalProvider>
                        <StatefulApp {...props} />
                    </ModalProvider>
                </SnackbarProvider>
            </ThemeProvider>
        </LanguageProvider>
    );
}

export default App;
